import { ICaremapNoteResponse } from '../model/NotesClient';
import { IAttachment } from '../generic-notes/attachments/IAttachment';

export type NoteField = 'title' | 'html' | 'isPrivate' | 'tags';
const CONFLICTABLE_FIELDS: NoteField[] = ['title', 'html', 'isPrivate', 'tags'];

export interface INoteFieldConflict { field: NoteField; base: unknown; mine: unknown; theirs: unknown; }
export interface INoteMergeResult { merged: ICaremapNoteResponse; conflicts: INoteFieldConflict[]; }

export class NoteConflictDetector {
    getDirtyFields = (base: ICaremapNoteResponse, mine: ICaremapNoteResponse): NoteField[] =>
        CONFLICTABLE_FIELDS.filter(field => !this.deepEqual(mine[field], base[field]));

    // Whether the attachments differ, which is a DIRTY question rather than a conflict one.
    //
    // Attachments are deliberately absent from CONFLICTABLE_FIELDS, because two people adding
    // different files is two additions rather than a disagreement to resolve — see mergeAttachments
    // for what that means in practice. But adding a file is still a change the caregiver expects to
    // save, and treating "conflictable" as if it meant "changed" left an edit that ONLY added an
    // attachment permanently unsaveable — Save stayed disabled with nothing on screen to say why.
    hasAttachmentChanges = (base: ICaremapNoteResponse, mine: ICaremapNoteResponse): boolean =>
        !this.deepEqual(mine.attachments, base.attachments);

    // Attachments merge as a union, which is the half of "not a conflict" that is easy to leave
    // unwritten. Inheriting the server's list wholesale loses a file the caregiver attached moments
    // earlier, and loses it silently: no conflict is raised (that is the whole point of keeping
    // attachments unconflictable), so the save proceeds and reports success while the file is
    // simply gone. A merge happens on the ordinary paths, not just under contention — a co-owner's
    // edit arriving over the websocket, or the automatic retry after a 409 — so this is reachable
    // without anyone doing anything unusual.
    //
    // Identity is the url: it addresses the uploaded object itself, so it is unique per file and
    // stable across the two copies, which the array index is not.
    //
    // Deleting is an intention just as much as adding is, and it has to be honoured from BOTH
    // sides or the union quietly reverses one of them. Subtracting only my own removals looks
    // symmetrical and is not: their delete arrives as an absence from their list, my stale copy
    // still holds the file, and the append loop puts it straight back. That is worse than the loss
    // this merge exists to prevent, because removing an attachment is frequently a privacy action
    // — a document uploaded in error, naming somebody else — and restoring it re-fans it out to
    // every collaborator on a note the other person never re-edited.
    //
    // So a file survives only if NEITHER side removed it, while an addition from EITHER side is
    // kept. Both halves are computed against `base`, which is the only copy that says what was
    // there before either of us touched it.
    mergeAttachments = (params: { base: ICaremapNoteResponse; mine: ICaremapNoteResponse; theirs: ICaremapNoteResponse }): IAttachment[] => {
        const { base, mine, theirs } = params;
        const urlsOf = (note: ICaremapNoteResponse) => new Set((note.attachments || []).map(attachment => attachment.url));

        const baseUrls = urlsOf(base);
        const mineUrls = urlsOf(mine);
        const theirUrls = urlsOf(theirs);

        const removedByMe = [...baseUrls].filter(url => !mineUrls.has(url));
        const removedByThem = [...baseUrls].filter(url => !theirUrls.has(url));

        // Copied rather than aliased: the merged array outlives the two notes it was built from,
        // and sharing an element would mean editing one attachment silently edited another note's.
        const merged = (theirs.attachments || [])
            .filter(attachment => !removedByMe.includes(attachment.url))
            .map(attachment => ({ ...attachment }));
        const present = new Set(merged.map(attachment => attachment.url));

        for (const attachment of mine.attachments || []) {
            if (!present.has(attachment.url) && !removedByThem.includes(attachment.url)) {
                merged.push({ ...attachment });
                present.add(attachment.url);
            }
        }

        return merged;
    };

    getRemoteChangedFields = (base: ICaremapNoteResponse, theirs: ICaremapNoteResponse): NoteField[] =>
        CONFLICTABLE_FIELDS.filter(field => !this.deepEqual(theirs[field], base[field]));

    detect = ({ base, mine, theirs }: { base: ICaremapNoteResponse; mine: ICaremapNoteResponse; theirs: ICaremapNoteResponse; }): INoteMergeResult => {
        const dirty = this.getDirtyFields(base, mine);
        const remoteChanged = this.getRemoteChangedFields(base, theirs);
        const conflicts: INoteFieldConflict[] = [];
        // start from theirs so we inherit server-managed fields (version, updatedAt, links,
        // reactions), then re-apply MY uncommitted field edits
        const merged: ICaremapNoteResponse = this.clone(theirs);
        merged.attachments = this.mergeAttachments({ base, mine, theirs });
        for (const field of CONFLICTABLE_FIELDS) {
            const isMine = dirty.includes(field);
            const isTheirs = remoteChanged.includes(field);
            // A real conflict requires the two sides to DISAGREE. When we both changed the field
            // to the same value there is nothing to resolve — common cases are the server echoing
            // my own save back to my still-open editor (a self-echo, where base is briefly stale)
            // and a co-owner making the identical edit. Without this equality guard we'd surface a
            // phantom "changed by someone else" conflict whose mine/theirs are identical, blocking
            // save. merged already holds theirs (=== mine here), so fall through to the no-op path.
            if (isMine && isTheirs && !this.deepEqual(mine[field], theirs[field])) {
                conflicts.push({ field, base: base[field], mine: mine[field], theirs: theirs[field] });
                this.assignField(merged, field, mine[field]); // hold mine until resolved
            } else if (isMine) {
                this.assignField(merged, field, mine[field]);
            }
            // else: only they changed it, neither did, or we both made the same change — merged already has theirs
        }
        return { merged, conflicts };
    };

    private assignField = (note: ICaremapNoteResponse, field: NoteField, value: unknown) => {
        // index assignment across a union of value types; value comes from the same field on a
        // sibling note, so the runtime type always matches (mirrors CareMapMerger's pattern)
        (note[field] as unknown) = value;
    };
    private clone = (note: ICaremapNoteResponse): ICaremapNoteResponse => JSON.parse(JSON.stringify(note));
    private deepEqual = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
}
